define(/** @lends InputSearch */ function (require) {

    "use strict";

    /////////////////////////////////////////////////////////////////////////////
    //                              Dependencies                               //
    /////////////////////////////////////////////////////////////////////////////
    var _ = require("underscore");
    var $ = require("jquery");
    var Marionette = require("marionette");
    var Modernizr = require('modernizr');
    var template = require('ui/input-search/templates/InputSearch.tmpl');
    var InputSearchModel = require('ui/input-search/js/models/InputSearchModel');
    var EMPTY_VALUE = "";
    require('ui.plugins/jquery.inputCursor');

    /**
     * Creates a new instance of the InputSearch class.
     *
     * @example
     * var InputSearch = require('ui/input-search/InputSearch');
     *
     * var searchInput = new InputSearch({
	 *     el: this.ui.searchInputPlaceholder
	 * });
     *
     * @constructor InputSearch
     *
     * @param {InputSearch.Options} options
     *
     * @requires backbone
     * @requires marionette
     * @requires underscore
     *
     * @augments Marionette.ItemView
     *
     * @classdesc An input field used for searching content. enables placing the  magnifying glass icon on the left or in the right od the search field.
     * */
    var InputSearch = Marionette.ItemView.extend({
        errorMsg        :null,
        withUIComponents:true,
        placeholderIsSet:false,
        ui: {
            input : "input",
            container: ".input-search-container",
            iconLeft: ".input-search-container .icon-left",
            iconRight: ".input-search-container .icon-right"
        },
        events          :{
            "blur input"   :function (e) {
                this._onInputBlur(e);
            },
            "cut input"    :function () {
                this.handleDelayedChange();
            },
            "paste input"  :function () {
                this.handleDelayedChange();
            },
            "keydown input":function (e) {
                this.handleKeyDownEvent(e);
            },
            "keyup input"  :function (e) {
                this.handleChange(e);
            },
            "focus input"  :function () {
                this._onInputFocus(this);
            },
            "clickoutside" : function() {
                this._handleClickOutside();
            },
            "click @ui.iconRight" : function() {
                this._handleClickIcon();
            },
            "click @ui.iconLeft" : function() {
                this._handleClickIcon();
            }
        },
        modelEvents:{
            "change:value":"handleInputValue",
            "change:placeholder": "handleInputValue"
        },

        initialize: function(options) {
            // make sure options is a valid object.
            if (!options || !_.isPlainObject(options)) {
                throw "InputSearch :: invalid argument: options is illegal.";
            }

            if (options.el) {
                options.placeholder = !_.isUndefined(this.$el.data('placeholder')) ? this.$el.data('placeholder') : options.placeholder;
            }

            options.value = !_.isUndefined(options.value) ? options.value : '';
            options.placeholder = !_.isUndefined(options.placeholder) ? options.placeholder : '';

            var inputSearchModel = new InputSearchModel();
            inputSearchModel.set(options);

            this.permutation = options.permutation || InputSearch.Permutation.A;
            this.theme = options.theme || InputSearch.THEME.DEFAULT;
            this.inputType = options.inputType;
            this.template = options.template || template;
            this.model = inputSearchModel;

            this.autoFocus = options.autoFocus || false;
            this.disabled = options.disabled || false;
            this.lastKeyDown = null;
        },

        onRender: function() {
            this.ui.container.addClass("permutation" + this.permutation);
            this.ui.input.addClass(this.theme);
            this.handleInputValue();
            this.setFocus(this.autoFocus);
            this.handleInputWidth();
        },

        handleInputValue: function() {
            var val = this.getValue();

            if (this.ui.input.val() !== val) {
                this.ui.input.val(val);
            }

            if (EMPTY_VALUE === this.getValue()) {
                this.showPlaceholder();
            }
            else {
                this.hidePlaceholder({silent:true});
            }
        },

        /**
         * Get input value.
         */
        getValue: function() {
            return this.model.get(InputSearch.VALUE);
        },

        /**
         * Set input value.
         *
         * @param: value for input field.
         */
        setValue: function(value, options) {
            options = options || {};
            options.silent = _.has(options, "silent") ? options.silent : true;
            this._setValue(value, options);
            this.handleInputWidth();
        },

        _setValue: function(value, options) {
            options = options || {};

            this.model.set(InputSearch.VALUE, String(value));
            //input is converting automatically the value to string, to avoid unneccesssary model change events we keep it as string too

            if (!options.silent) {
                this.trigger(InputSearch.TRIGGERING.CHANGE, this.getValue());
            }
        },

        /**
         * Set input placeholder.
         *
         * @param: placeholder for input field.
         */
        setPlaceholder: function(val) {
            this.model.set({"placeholder":val});
        },

        showPlaceholder: function() {
            if (!Modernizr.input.placeholder && !this.ui.input.is(":focus")) {
                if (!this.placeholderIsSet) {
                    this.placeholderIsSet = true;
                    this.ui.input.addClass('placeholder');
                }
                this.ui.input.val(this.ui.input.attr('placeholder'));
            }
        },

        hidePlaceholder: function(options) {
            options = options || {};
            if (!Modernizr.input.placeholder) {
                if (this.placeholderIsSet) {
                    this.ui.input.removeClass('placeholder');
                    this.placeholderIsSet = false;
                    if (!options.silent) {
                        this.ui.input.val(EMPTY_VALUE);
                    }
                }
            }
        },

        setFocus: function(focus) {
            if (focus) {
                setTimeout(_.bind(function () {
                    this._focusInput();
                }, this), 1);
            }
        },

        blurInput: function() {
            this.ui.input.blur();
        },

        handleKeyDownEvent: function(e) {
            this.lastKeyDown = (e && e.keyCode) ? e.keyCode : null;

            if (e && e.keyCode === $.ui.keyCode.ENTER) {
                this.trigger(InputSearch.TRIGGERING.ENTER_PRESSED, {
                    value: this.getValue(),
                    event: e
                });
            }

            if (e && (e.keyCode === $.ui.keyCode.UP || e.keyCode === $.ui.keyCode.DOWN)) {
                this.trigger(InputSearch.TRIGGERING.ARROW_PRESSED, {
                    isUp: (e.keyCode === $.ui.keyCode.UP),
                    value: this.getValue(),
                    event: e
                });
            }
        },

        _focusInput: function() {
            if (this.ui.input && this.ui.input.inputcursor) {
                if (this.model.get("id")) {
                    this.ui.input.inputcursor('focusEnd');
                }
                else {
                    this.ui.input.inputcursor('selectAll');
                }
            }
        },

        _handleClickIcon: function() {
            var searchString = this.ui.input.val();
            this.trigger(InputSearch.TRIGGERING.ONCLICKICON, {value: searchString});
        },

        _handleClickOutside: function() {
            this.trigger(InputSearch.TRIGGERING.ONCLICKOUTSIDE);
        },
        _onInputFocus: function() {
            this.ui.input.attr("placeholder", this.model.get("placeholder"));
            this.lastKeyDown = null;
            this.trigger(InputSearch.TRIGGERING.ONFOCUS);
        },
        _onInputBlur: function (event) {
            this.ui.input.attr("placeholder", "");
            var trimmedValue = this.ui.input.val().trim();
            if (trimmedValue !== this.getValue()) {
                this.setValue(trimmedValue);
            }
            else {
                this.handleInputValue();
            }
            this.trigger(InputSearch.TRIGGERING.ONBLUR, {
                value: this.getValue(),
                event: event });
        },
        handleDelayedChange: function () {
            var that = this;
            setTimeout(function () { //right click paste/cut value is available on the element only after timeout
                that.handleChange(null, this);
            }, 0);
        },

        handleChange: function () {
            var val = this.ui.input.val();
            this.setValue.call(this, val, {silent: false});
            this.handleInputWidth();
            this.trigger(InputSearch.TRIGGERING.ONKEYUP, {value: val});
        },
        handleInputWidth: function() {
            var val = this.ui.input.val();
            this.ui.input.toggleClass("focus", !_.isEmpty(val));
        }
}); // EO InputSearch


    InputSearch.Type = InputSearchModel.Type;

    /**
     * The available view permutations of the input-fields-plus.
     *
     * @enum
     * @readonly
     */
    InputSearch.Permutation = {
        A:'A', B:'B'
    }; // EO InputField.Type

    InputSearch.THEME = {
        DEFAULT: "default-theme",
        NIGHT: "night-theme"
    };

    InputSearch.TRIGGERING = {
        CHANGE       :"change",
        ARROW_PRESSED:"arrowPressed",
        ENTER_PRESSED:"enterPressed",
        ONFOCUS: "onfocus",
        ONBLUR: "onblur",
        ONCLICKOUTSIDE: "onclickoutside",
        ONCLICKICON: "onClickIcon",
        ONKEYUP: "onKeyUp"
    };

    InputSearch.VALUE = "value";

    // return the input-field component.
    return InputSearch;
});

